import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Cpu,
  FileText,
  Bell,
  Shield,
  Settings,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Zap,
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { hasUnseenUpdates } from '../shared/WhatsNewModal'
import { cn } from '../../lib/utils'
import Logo from '../shared/Logo'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Home', end: true },
  { to: '/devices', icon: Cpu, label: 'Devices' },
  { to: '/proposals', icon: FileText, label: 'Proposals' },
  { to: '/alerts', icon: Bell, label: 'Alerts' },
  { to: '/support-tickets', icon: Headphones, label: 'Support' },
  { to: '/vpp', icon: Zap, label: 'VPP' },
]

const adminItems = [
  { to: '/admin', icon: Shield, label: 'Admin' },
]

const bottomItems = [
  { to: '/settings', icon: Settings, label: 'Settings' },
]

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'owner' || user?.role === 'admin'

  return (
    <aside
      aria-label="Main navigation"
      className={cn(
        'fixed left-0 top-0 bottom-0 z-40 bg-bg-sidebar border-r border-border flex flex-col transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b border-border shrink-0">
        <Logo size={32} className="shrink-0" />
        {!collapsed && (
          <span className="ml-3 font-semibold text-text-primary text-sm whitespace-nowrap">
            ESI Energy Conductor
          </span>
        )}
      </div>

      {/* Nav */}
      <nav aria-label="Primary" className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <SidebarLink key={item.to} {...item} collapsed={collapsed} />
        ))}
        {isAdmin && adminItems.map((item) => (
          <SidebarLink key={item.to} {...item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Bottom */}
      <div className="py-3 px-2 space-y-1 border-t border-border">
        {bottomItems.map((item) => (
          <SidebarLink key={item.to} {...item} collapsed={collapsed} badge={item.to === '/settings' && hasUnseenUpdates()} />
        ))}

        <button
          onClick={onToggle}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </span>
          {!collapsed && <span className="text-sm">Collapse</span>}
        </button>
      </div>
    </aside>
  )
}

interface SidebarLinkProps {
  to: string
  icon: React.ComponentType<{ size?: number }>
  label: string
  collapsed: boolean
  end?: boolean
  badge?: boolean
}

function SidebarLink({ to, icon: Icon, label, collapsed, end, badge }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm',
          isActive
            ? 'bg-accent/15 text-accent font-medium'
            : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
        )
      }
      title={label}
    >
      <span className="relative w-5 h-5 flex items-center justify-center shrink-0">
        <Icon size={18} />
        {badge && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-accent border-2 border-bg-sidebar" />
        )}
      </span>
      {!collapsed && <span className="whitespace-nowrap">{label}</span>}
    </NavLink>
  )
}
